import { DateTimeEditor } from '@/components/UI/SpreadSheet/editors/DateTimeEditor';
import type { CustomColumnSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import { capitalise, renderMetadataValue } from '@/helpers';
import { _isNotEmpty, _isNil, _notNil, safelyParseJson } from '@/littledash';
import type { Animal } from '@/model/Animal.model';
import type { MetadataField } from '@/model/Metadata.model';
import type { BulkSamplesFormData, SampleData, SampleDetails } from '@/model/Sample.model';
import { defaultMetadataToColumnSettingMapper, formatColumnSettingsDataValue } from '@/support/hot';
import { TimepointEditor } from '@/components/UI/SpreadSheet/editors/TimepointEditor';

export const toFiniteNumberOrNull = (value: unknown): number | null => {
  if (_isNil(value)) {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const getSampleMeta = (meta: Record<string, any>, metadata: Array<MetadataField>) =>
  Object.entries(meta).reduce<Record<string, string>>((acc, [metaSlug, value]) => {
    const glossaryMeta = metadata.find(({ slug }) => slug === metaSlug);
    if (_notNil(glossaryMeta)) {
      if (glossaryMeta.field_type === 'date') {
        acc[formatColumnSettingsDataValue(glossaryMeta.title)] = value;
      } else if (Array.isArray(value)) {
        acc[formatColumnSettingsDataValue(glossaryMeta.title)] = value.toString();
      } else if (Array.isArray(safelyParseJson(value))) {
        acc[formatColumnSettingsDataValue(glossaryMeta.title)] = JSON.parse(value).join(',');
      } else {
        acc[formatColumnSettingsDataValue(glossaryMeta.title)] = renderMetadataValue({
          value,
          field_type: glossaryMeta.field_type,
        });
      }
    }
    return acc;
  }, {});

const getSampleDetails = (details: SampleDetails) =>
  Object.entries(details).reduce<Record<string, string>>((acc, [detail, value]) => {
    acc[detail] = value.value;
    return acc;
  }, {});

export const generateSampleData = (
  subjects: Array<Animal>,
  metadata: Array<MetadataField>,
  sampleDetails: BulkSamplesFormData
) =>
  subjects.reduce<Array<SampleData>>((acc, { id: subject_id, cage, name }) => {
    const { sample_type, date, comments, details, timepoint, meta = {} } = sampleDetails;

    const sample: SampleData = {
      cageName: cage?.name,
      subject_id,
      subjectName: name ?? '',
      sampleType: sample_type.title ?? '',
      date,
      comments,
      meta: _isNotEmpty(meta) ? getSampleMeta(meta, metadata) : {},
      timepoint,
      details: getSampleDetails(details ?? {}),
    };

    acc.push(sample);
    return acc;
  }, []);

export const generateColumns = (
  sampleDetails: BulkSamplesFormData,
  metadata: Array<MetadataField>,
  plannedSamplesEnabled = false
) => {
  const columns: Array<CustomColumnSettings> = [
    {
      data: 'cageName',
      readOnly: true,
      type: 'text',
      value: 'Cage',
      width: 150,
    },
    {
      data: 'subjectName',
      readOnly: true,
      type: 'text',
      value: 'Animal name',
      width: 150,
    },
    {
      data: 'sampleType',
      readOnly: true,
      type: 'text',
      value: 'Sample type',
      width: 150,
    },
    {
      data: 'date',
      readOnly: false,
      type: DateTimeEditor.type,
      value: 'Date taken',
      width: 200,
    },
  ];

  const colHeaders = ['Cage', 'Animal name', 'Sample type', 'Date taken'];

  Object.entries(sampleDetails.details ?? {}).forEach(([detail, value]) => {
    columns.push({
      data: `details.${detail}`,
      readOnly: false,
      type: 'numeric',
      value: `${capitalise(detail)} (${value.unit})`,
      width: 150,
    });
    colHeaders.push(`${capitalise(detail)} (${value.unit})`);
  });
  if (plannedSamplesEnabled) {
    colHeaders.push('Timepoint');
    columns.push({
      // @ts-expect-error: untyped internal HoT function
      data: (row: SampleData, value: string) => {
        if (typeof value === 'undefined') {
          return row.timepoint;
        }
        row.timepoint = Array.isArray(value) ? value[0] : undefined;
      },
      readOnly: false,
      type: TimepointEditor.type,
      singleValue: true,
      allow_zero: true,
      allow_negative: true,
    });
  }

  colHeaders.push('Comments');
  columns.push({
    data: 'comments',
    readOnly: false,
    type: 'text',
    value: 'Comments',
    width: 150,
    validator: function (value: string, callback: (valid: boolean) => void) {
      callback((value ?? '').length <= 255);
    },
    validationErrorMessage: () => 'Comments have a max length of 255 characters',
  });

  if (_isNotEmpty(metadata)) {
    metadata.forEach((meta) => {
      const { columnHeader, columnSettings } = defaultMetadataToColumnSettingMapper(meta);
      colHeaders.push(columnHeader);
      columns.push(columnSettings);
    });
  }

  return { colHeaders, columns };
};
